import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePharmacyContext } from "@/contexts/PharmacyContext";
import { PharmacySelector } from "./PharmacySelector";

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { currentPharmacy, availablePharmacies, refreshPharmacies } =
    usePharmacyContext();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const desktopItemClasses = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 flex-shrink-0 ` +
    (isActive
      ? "bg-brand-light text-brand shadow-sm border border-brand"
      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900");

  const mobileItemClasses = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ` +
    (isActive
      ? "bg-brand-light text-brand"
      : "text-gray-600 hover:bg-gray-100");

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Modern Navbar - Full Width */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-md">
        <nav className="w-full">
          <div className="flex items-center justify-between min-h-14 md:min-h-16 max-w-full gap-1 md:gap-2 lg:gap-4 py-2 px-2 sm:px-4 lg:px-8">
            {/* Mobile Menu Toggle - Left Side (up to lg) */}
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
              aria-label="Toggle menu"
            >
              <svg
                className="w-5 h-5 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {isMobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>

            {/* Logo/Brand Section */}
            <Link
              to="/dashboard"
              className="flex items-center gap-1.5 sm:gap-2 md:gap-3 hover:opacity-80 transition-opacity flex-shrink-0"
              onClick={closeMobileMenu}
            >
              <img
                src="/voyaxhealth-cropped.png"
                alt="Voyax Health Logo"
                className="w-7 h-7 sm:w-9 sm:h-9 md:w-12 md:h-12 object-contain"
              />
              <div>
                <h1 className="text-sm sm:text-base md:text-lg font-bold text-gray-900">
                  Voyax Health
                </h1>
                <p className="text-xs sm:text-sm md:text-sm text-teal-600 font-medium -mt-0.5">
                  Pharmacy Portal
                </p>
              </div>
            </Link>

            {/* Desktop Navigation Links - Large screens only */}
            <div className="hidden lg:flex items-center gap-2 flex-1 justify-center">
              {/* Pharmacy Selector - Desktop */}
              <div className="flex-shrink-0">
                <PharmacySelector
                  currentPharmacy={currentPharmacy}
                  availablePharmacies={availablePharmacies}
                  onPharmacyChange={refreshPharmacies}
                />
              </div>

              <NavLink
                to="/dashboard"
                onClick={closeMobileMenu}
                className={desktopItemClasses}
                end
              >
                <svg
                  className="w-5 h-5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                <span className="whitespace-nowrap">Dashboard</span>
              </NavLink>

              <NavLink
                to="/approved"
                onClick={closeMobileMenu}
                className={desktopItemClasses}
                end
              >
                <svg
                  className="w-5 h-5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="whitespace-nowrap">Approved</span>
              </NavLink>
            </div>

            {/* User Section */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* User Info - Large screens only */}
              <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 text-white text-xs font-bold shadow-sm flex-shrink-0">
                  {user?.email?.charAt(0).toUpperCase()}
                </div>
                <div className="text-xs min-w-0">
                  <p className="font-semibold text-gray-700 leading-tight truncate">
                    {user?.email?.split("@")[0]}
                  </p>
                  <p className="text-gray-500 leading-tight whitespace-nowrap">
                    Pharmacist
                  </p>
                </div>
              </div>

              {/* Logout Button - Hidden on mobile (available in dropdown), visible md and up */}
              <button
                onClick={handleLogout}
                className="hidden md:flex items-center gap-2 px-2 sm:px-3 py-2 bg-error hover:bg-red-600 text-white rounded-lg text-sm font-semibold transition-all duration-200 shadow-sm hover:shadow-md flex-shrink-0"
                aria-label="Logout"
              >
                <svg
                  className="w-5 h-5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
                <span className="whitespace-nowrap">Logout</span>
              </button>
            </div>
          </div>

          {/* Mobile Menu - Full Screen Overlay */}
          {isMobileMenuOpen && (
            <div className="lg:hidden fixed inset-0 z-50 bg-white">
              {/* Mobile Menu Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <img
                    src="/voyaxhealth-cropped.png"
                    alt="Voyax Health Logo"
                    className="w-8 h-8 object-contain"
                  />
                  <div>
                    <h2 className="text-sm font-bold text-gray-900">
                      Voyax Health
                    </h2>
                    <p className="text-xs text-teal-600 font-medium">
                      Pharmacy Portal
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeMobileMenu}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  aria-label="Close menu"
                >
                  <svg
                    className="w-6 h-6 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Mobile Menu Content */}
              <div className="overflow-y-auto h-full pb-20">
                <div className="p-4 space-y-3">
                  {/* Pharmacy Selector - Mobile */}
                  <div className="mb-4">
                    <PharmacySelector
                      currentPharmacy={currentPharmacy}
                      availablePharmacies={availablePharmacies}
                      onPharmacyChange={refreshPharmacies}
                    />
                  </div>

                  {/* Navigation Links */}
                  <NavLink
                    to="/dashboard"
                    onClick={closeMobileMenu}
                    className={mobileItemClasses}
                    end
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                      />
                    </svg>
                    <span className="text-base">Dashboard</span>
                  </NavLink>

                  <NavLink
                    to="/approved"
                    onClick={closeMobileMenu}
                    className={mobileItemClasses}
                    end
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span className="text-base">Approved Forms</span>
                  </NavLink>

                  {/* Mobile User Info */}
                  <div className="flex items-center gap-3 px-4 py-4 mt-6 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 text-white font-bold shadow-sm">
                      {user?.email?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-800 truncate">
                        {user?.email}
                      </p>
                      <p className="text-sm text-gray-500">Pharmacist</p>
                    </div>
                  </div>

                  {/* Mobile Logout */}
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center gap-3 px-4 py-3.5 mt-4 bg-error hover:bg-red-600 text-white rounded-lg font-semibold transition-all shadow-md"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                      />
                    </svg>
                    <span className="text-base">Logout</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Main Content */}
      <main className="w-full px-4 sm:px-6 lg:px-8 py-6">{children}</main>
    </div>
  );
}
